/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, {
  ChangeEvent,
  FormEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import getUpdatePasswordValidator from "../../utils/validator";
import { IRootState } from "../../config/reducers";
import { LoginAction } from "./ducks/slices";
import {
  AppAlert,
  ErrorMessageValidator,
  LaddaButton,
  NestedErrorMessage,
  PasswordRequirementTooltip,
} from "@advapacs/common";
import { passwordReset, verifyToken } from "./ducks/operations";
import { useAppDispatch } from "../../config/store";

const ResetPasswordForm: FC = () => {
  const { t } = useTranslation("login");
  const { search } = useLocation();
  const target = useRef(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const validator = useMemo(() => {
    return getUpdatePasswordValidator(t);
  }, [t]);

  const token: string | null = new URLSearchParams(search).get("token");
  const [stateForm, setStateForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [, setError] = useState({});
  const { loading, passwordResetComplete, passwordResetErrorMessage } =
    useSelector((state: IRootState) => state.loginReducer);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setStateForm({ ...stateForm, [name]: value });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!validator.allValid()) {
      validator.showMessages();
      setError({});
      return;
    }
    dispatch(passwordReset({ ...stateForm, token }));
  };

  useEffect(() => {
    return () => {
      dispatch(LoginAction.clearPasswordReset());
    };
  }, [dispatch]);
  useEffect(() => {
    if (token) {
      dispatch(verifyToken(token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      <form>
        <h5 className="text-center mb-2">{t("change_your_password")}</h5>
        {passwordResetComplete && (
          <>
            <div className="text-center mb-2">
              {passwordResetErrorMessage?.message ||
                t("password_reset_confirmation")}
            </div>
            <Link
              className="btn btn-light mt-2 btn-block btn-ladda"
              to="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              <i className="fa fa-angle-double-left ml-2" />
              {t("back_to_login")}
            </Link>
          </>
        )}
        {!passwordResetComplete && (
          <>
            {passwordResetErrorMessage && (
              <AppAlert.ErrorAlert
                error={passwordResetErrorMessage?.message}
                dismissible
                onClose={() => dispatch(LoginAction.clearPasswordReset())}
              />
            )}

            <div
              className="form-group form-group-feedback form-group-feedback-left"
              ref={target}
            >
              <PasswordRequirementTooltip placement="right">
                <input
                  id="password"
                  type="password"
                  className="form-control"
                  placeholder={t("password")}
                  name="password"
                  value={stateForm.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
              </PasswordRequirementTooltip>
              <ErrorMessageValidator
                errorMessage={validator.message(
                  `password`,
                  stateForm.password,
                  "required|passwordPattern"
                )}
              />
              <div className="form-control-feedback">
                <i className="icon-lock2 text-muted" />
              </div>
            </div>
            <div className="form-group form-group-feedback form-group-feedback-left">
              <input
                type="password"
                className="form-control"
                placeholder={t("confirm_password")}
                name="confirmPassword"
                value={stateForm.confirmPassword}
                onChange={handleInputChange}
                autoComplete="new-password"
              />
              <ErrorMessageValidator
                errorMessage={validator.message(
                  `confirmPassword`,
                  stateForm.confirmPassword,
                  "required"
                )}
              />
              <ErrorMessageValidator
                errorMessage={validator.message(
                  ``,
                  stateForm,
                  "comparePassword"
                )}
              />
              <div className="form-control-feedback">
                <i className="icon-lock2 text-muted" />
              </div>
            </div>
          </>
        )}
        {(passwordResetErrorMessage as NestedErrorMessage)?.errors && (
          <div className="form-group">
            <div className="alert bg-warning text-white alert-styled-left error-container">
              {(passwordResetErrorMessage as NestedErrorMessage)?.errors &&
                (passwordResetErrorMessage as NestedErrorMessage)?.errors[0]
                  .message}
            </div>
          </div>
        )}
        <div className="form-group">
          {!passwordResetComplete && (
            <>
              <LaddaButton
                className="btn btn-primary btn-block btn-ladda btn-ladda-spinner"
                data-style="slide-right"
                type="submit"
                disabled={loading}
                loading={loading}
                onClickHandler={submit}
                btnName={t("reset_password")}
              />
              <Link className="btn btn-light mt-2 btn-block btn-ladda" to="/">
                <i className="fa fa-angle-double-left ml-2" />
                {t("back_to_login")}
              </Link>
            </>
          )}
        </div>
      </form>
    </>
  );
};
export default ResetPasswordForm;
